import { ImageOff, ImageUp, Upload } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { TextField } from '~/components/form';
import { Alert, Button, FormActions, Modal } from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useInsertDuplicateMlsMutation, useUploadLogoMutation } from '~/modules/query/mls';
import type { MlsRow } from '~/components/Constants/mlsConstants';
import { feedWrite, mlsKeyFor } from '~/components/pages/mls/components/feedActions';

const SaveError = ({ message }: { message: string | null }) =>
  message ? (
    <Alert variant="error">
      <p className="text-sm text-gray-800 dark:text-white/90">{message}</p>
    </Alert>
  ) : null;

const duplicateSchema = z.object({
  newId: z.string().trim().min(1, 'Enter the new platform feed ID.'),
});

/** Modal that copies this feed's Mongo configuration to a new feed id. */
export function FeedDuplicateModal({
  feed,
  isOpen,
  onClose,
}: {
  feed: MlsRow;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [insertDuplicateMls] = useInsertDuplicateMlsMutation();
  const [newId, setNewId] = useState('');
  const v = useFormValidation(duplicateSchema, { newId }, () => 'feed-duplicate-id');
  const { submit, busy, error, clearError } = useRecordSave<string>({
    save: (id) => feedWrite(insertDuplicateMls({ mlsid: mlsKeyFor(feed), newMlsId: id }).unwrap()),
    success: 'Feed duplicated',
    failure: 'The feed could not be duplicated. Try again.',
  });

  const close = () => {
    setNewId('');
    v.reset();
    clearError();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="small"
      title="Duplicate feed"
      description="Creates a new feed from this feed's Mongo configuration."
      busy={busy}
      testId="feed-duplicate-modal"
      footer={
        <FormActions
          form="feed-duplicate-form"
          discardLabel="Cancel"
          onDiscard={close}
          saveLabel="Duplicate feed"
          busyLabel="Duplicating…"
          busy={busy}
          saveTestId="feed-duplicate"
          discardTestId="feed-duplicate-cancel"
        />
      }
    >
      <form
        id="feed-duplicate-form"
        noValidate
        className="space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!v.submit()) return;
          if (await submit(newId.trim())) close();
        }}
      >
        <SaveError message={error} />
        <TextField
          id="feed-duplicate-id"
          testId="mls-copy-feed-id"
          label="New platform feed ID"
          required
          value={newId}
          onChange={setNewId}
          onBlur={() => v.touch('newId')}
          error={v.errors.newId}
          helperText={`Copies the Mongo configuration from ${feed._id}.`}
        />
      </form>
    </Modal>
  );
}

/** The feed's logo in a preview tile, with a picker and upload for a new one. */
export function FeedLogo({ feed }: { feed: MlsRow }) {
  const [uploadLogo] = useUploadLogoMutation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const { submit, busy, error } = useRecordSave<File>({
    save: (picked) => feedWrite(uploadLogo({ mlsid: feed.mlsid as string, file: picked }).unwrap()),
    success: 'Logo uploaded',
    failure: 'The logo could not be uploaded. Try again.',
  });

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => (previewUrl ? URL.revokeObjectURL(previewUrl) : undefined), [previewUrl]);

  const clear = () => {
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };
  const shown = previewUrl ?? (feed.iconUrl && failedSrc !== feed.iconUrl ? feed.iconUrl : null);

  return (
    <div className="space-y-4">
      <SaveError message={error} />
      <div className="flex flex-wrap items-center gap-5">
        <div className="flex size-24 shrink-0 items-center justify-center rounded-xl bg-gray-50 p-3 inset-ring inset-ring-gray-200 dark:bg-white/5 dark:inset-ring-gray-800">
          {shown ? (
            <img
              src={shown}
              alt={file ? 'New logo preview' : `${feed.name ?? feed._id} logo`}
              className="max-h-full max-w-full object-contain"
              onError={() => !file && feed.iconUrl && setFailedSrc(feed.iconUrl)}
            />
          ) : (
            <ImageOff className="size-6 text-gray-400 dark:text-gray-500" aria-label="No logo" />
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <div>
            <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
              {file ? file.name : shown ? 'Current logo' : 'No logo yet'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {feed.mlsid
                ? 'PNG, JPG or GIF. Resized to fit within 150×150.'
                : 'This feed has no MLS ID to attach a logo to.'}
            </p>
          </div>
          {feed.mlsid && (
            <div className="flex flex-wrap items-center gap-2">
              {file ? (
                <>
                  <Button
                    size="sm"
                    loading={busy}
                    testId="feed-logo"
                    startIcon={<Upload className="size-4" aria-hidden="true" />}
                    onClick={async () => {
                      if (await submit(file)) clear();
                    }}
                  >
                    Upload logo
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={clear}
                    testId="feed-logo-cancel"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  testId="feed-logo-choose"
                  startIcon={<ImageUp className="size-4" aria-hidden="true" />}
                  onClick={() => fileInput.current?.click()}
                >
                  {shown ? 'Replace logo' : 'Choose image'}
                </Button>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="hidden"
                aria-label="Logo file"
                data-testid="feed-logo-file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
